import { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { useLocalization } from "@i18n";
import { LoadingBlurDiv } from "@shared/components";

import {
  useInspectionCapabilitiesQuery,
  useInspectionChartQuery,
  useInspectionSummaryQuery,
  type InspectionCapabilities,
  type InspectionChartParams,
} from "../features/inspections/api/inspection";
import InspectionFinalizedKpiCard from "../features/inspections/components/molecules/InspectionFinalizedKpiCard";
import InspectionConservationStateBand from "../features/inspections/components/organisms/InspectionConservationStateBand";
import InspectionEvolutionChart from "../features/inspections/components/organisms/InspectionEvolutionChart";
import InspectionsTable from "../features/inspections/components/organisms/InspectionsTable";
import {
  lastNMonthsChartRange,
  type InspectionChartPeriodMonths,
} from "../features/inspections/utils/inspectionChartPeriod";
import "../features/inspections/styles/inspection-ui.css";

/** Sem capabilities (erro/pendente) a UI de escrita fica fechada. */
const CLOSED_CAPABILITIES: InspectionCapabilities = {
  view: false,
  create: false,
  update: false,
  delete: false,
  upload: false,
  download: false,
};

/**
 * Tela inicial do módulo Inspeção (inspection.md §6.1): cabeçalho, KPI
 * `Finalizadas`, gráfico multi-série (§6.5), faixa EC somente leitura (§6.1.1)
 * e a listagem com filtros/ações — layout espelhado no protótipo navegável.
 */
const InspectionStructurePage = () => {
  const { translate } = useLocalization();
  const { structureId } = useParams<{ structureId: string }>();

  const [chartPeriod, setChartPeriod] =
    useState<InspectionChartPeriodMonths>(12);
  const chartRange: InspectionChartParams = lastNMonthsChartRange(chartPeriod);

  const capabilitiesQuery = useInspectionCapabilitiesQuery(structureId);
  const summaryQuery = useInspectionSummaryQuery(structureId);
  const chartQuery = useInspectionChartQuery(structureId, chartRange, {
    placeholderData: keepPreviousData,
  });

  // Só o primeiro load de capabilities/summary trava a página. Troca do
  // chip 3/6/12 do gráfico não pode desmontar a tabela.
  const isPageLoading =
    (capabilitiesQuery.isPending && !capabilitiesQuery.data) ||
    (summaryQuery.isPending && !summaryQuery.data);

  if (isPageLoading) {
    return (
      <LoadingBlurDiv isLoading className="insp-page min-h-[60vh]">
        <p role="status" className="text-sm text-[var(--insp-muted,#808080)]">
          {translate("inspection.loading")}
        </p>
      </LoadingBlurDiv>
    );
  }

  const summary = summaryQuery.data;
  const capabilities = capabilitiesQuery.data?.capabilities ?? CLOSED_CAPABILITIES;

  const heading = summary
    ? `${summary.complexName} · ${summary.structureName} · ${translate("inspection.title")}`
    : translate("inspection.title");

  return (
    <div className="insp-page">
      <h1 className="insp-page-title">{heading}</h1>

      <div className="insp-overview-row">
        <InspectionFinalizedKpiCard
          finalizedCount={summary?.finalizedCount ?? null}
        />
        <InspectionEvolutionChart
          chart={chartQuery.data}
          isLoading={chartQuery.isFetching}
          errorMessage={chartQuery.error?.parsedMessage ?? null}
          periodMonths={chartPeriod}
          onPeriodChange={setChartPeriod}
        />
      </div>

      <InspectionConservationStateBand
        conservationState={summary?.conservationState}
      />

      {structureId ? (
        <InspectionsTable
          structureId={structureId}
          capabilities={capabilities}
        />
      ) : null}
    </div>
  );
};

export default InspectionStructurePage;
